import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, StatGridSkeleton, EmptyState } from "@/components/common";
import { TASK_STATUSES, label } from "@/lib/constants";
import { fmtCurrency, fmtDate, fromNow, isOverdue } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ListTodo, AlertTriangle, CheckCircle2, CalendarClock, FolderKanban, Users,
  TrendingUp, Headphones, Wallet, FileCheck, Clock,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const nav = useNavigate();
  const { currentOrg } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading || !data) return <div><PageHeader title="Genel Bakış" /><StatGridSkeleton /></div>;

  const isManager = ["owner", "admin", "manager", "super_admin"].includes(data.role);
  const isAdmin = ["owner", "admin", "super_admin"].includes(data.role);

  const statusData = TASK_STATUSES.map((s) => ({ name: s.label, value: data.status_counts[s.id] || 0, color: s.color })).filter((d) => d.value > 0);
  const workloadData = (data.workload || []).map((w) => ({ name: w.name.split(" ")[0], count: w.count }));
  const monthly = (data.monthly_series || []).map((m) => ({ month: m.month.slice(5), count: m.count }));

  return (
    <div className="space-y-6">
      <PageHeader title="Genel Bakış" subtitle={`${currentOrg?.name} • ${data.role_label}`} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ListTodo} label="Açık görevlerim" value={data.my_open_count} tone="indigo" testId="stat-my-open" />
        <StatCard icon={CalendarClock} label="Bugün teslim" value={data.my_due_today} tone="sky" testId="stat-due-today" />
        <StatCard icon={AlertTriangle} label="Geciken görevlerim" value={data.my_overdue} tone="rose" testId="stat-my-overdue" />
        <StatCard icon={Clock} label="Aktif zamanlayıcı" value={data.active_timer ? "Çalışıyor" : "Yok"} tone={data.active_timer ? "emerald" : "slate"} testId="stat-timer" />
      </div>

      {isManager && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={ListTodo} label="Toplam açık görev" value={data.total_open} tone="indigo" testId="stat-total-open" />
          <StatCard icon={AlertTriangle} label="Geciken görevler" value={data.total_overdue} tone="rose" testId="stat-total-overdue" />
          <StatCard icon={CheckCircle2} label="Tamamlanan" value={data.total_completed} tone="emerald" testId="stat-completed" />
          <StatCard icon={FileCheck} label="Bekleyen onaylar" value={data.pending_approvals} tone="amber" testId="stat-approvals" />
        </div>
      )}

      {isAdmin && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={FolderKanban} label="Toplam proje" value={data.total_projects} tone="indigo" testId="stat-projects" />
          <StatCard icon={AlertTriangle} label="Risk altındaki proje" value={data.projects_at_risk} tone="amber" testId="stat-projects-risk" />
          <StatCard icon={CheckCircle2} label="Tamamlanan görev" value={data.total_completed} tone="emerald" testId="stat-admin-completed" />
          <StatCard icon={FileCheck} label="Bekleyen onaylar" value={data.pending_approvals} tone="sky" testId="stat-admin-approvals" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-1">
          <h3 className="font-semibold mb-4">Duruma göre görevler</h3>
          {statusData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState title="Veri yok" />}
        </Card>

        {isManager ? (
          <Card className="p-5 lg:col-span-2">
            <h3 className="font-semibold mb-4">Ekip iş yükü</h3>
            {workloadData.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={workloadData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4f46e5" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState title="Veri yok" />}
          </Card>
        ) : (
          <Card className="p-5 lg:col-span-2">
            <h3 className="font-semibold mb-4">Açık görevlerim</h3>
            <div className="space-y-2">
              {data.my_tasks?.length ? data.my_tasks.map((t) => (
                <button key={t.id} onClick={() => nav(`/tasks?id=${t.id}`)} className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted text-left transition-colors">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: TASK_STATUSES.find((s) => s.id === t.status)?.color }} />
                  <span className="text-sm flex-1 truncate">{t.title}</span>
                  {t.overdue && <Badge variant="outline" className="text-rose-600 border-rose-200 text-[10px]">Gecikmiş</Badge>}
                  {t.due_date && <span className="text-xs text-muted-foreground">{fmtDate(t.due_date)}</span>}
                </button>
              )) : <EmptyState title="Açık görev yok" />}
            </div>
          </Card>
        )}
      </div>

      {isAdmin && monthly.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold mb-4">Aylık tamamlanan görevler</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold mb-3">Yaklaşan etkinlikler</h3>
          <div className="space-y-2">
            {data.upcoming_events?.length ? data.upcoming_events.map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                <CalendarClock className="h-4 w-4 text-indigo-500" />
                <span className="text-sm flex-1 truncate">{e.title}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(e.start)}</span>
              </div>
            )) : <EmptyState icon={CalendarClock} title="Etkinlik yok" />}
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold mb-3">Son bildirimler</h3>
          <div className="space-y-2">
            {data.recent_notifications?.length ? data.recent_notifications.map((n) => (
              <div key={n.id} className="p-2 rounded-lg hover:bg-muted">
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-muted-foreground">{fromNow(n.created_at)}</p>
              </div>
            )) : <EmptyState title="Bildirim yok" />}
          </div>
        </Card>
      </div>
    </div>
  );
}
